import React from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

const STATUS_PALETTE = {
  'Draft':           'bg-amber-500',
  'In Review':       'bg-blue-500',
  'Approved':        'bg-violet-500',
  'State Submitted': 'bg-cyan-500',
  'Launched':        'bg-emerald-500',
  'Learnworlds':     'bg-gray-400',
  'Archived':        'bg-gray-400',
};

const STATUS_BADGE_PALETTE = {
  'Draft':           'bg-amber-50 text-amber-700 border-amber-200',
  'In Review':       'bg-blue-50 text-blue-700 border-blue-200',
  'Approved':        'bg-violet-50 text-violet-700 border-violet-200',
  'State Submitted': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Launched':        'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Learnworlds':     'bg-gray-100 text-gray-600 border-gray-200',
  'Archived':        'bg-gray-100 text-gray-600 border-gray-200',
};

const ASSIGNMENT_PALETTE = {
  assigned:  'bg-amber-500',
  accessed:  'bg-blue-500',
  submitted: 'bg-emerald-500',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const palette = STATUS_BADGE_PALETTE[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${palette}`}>
      {status}
    </span>
  );
}

function KpiTile({ label, value, sublabel, href, accent = 'indigo' }) {
  const accents = {
    indigo:  'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber:   'from-amber-500 to-amber-600',
    blue:    'from-blue-500 to-blue-600',
    violet:  'from-violet-500 to-violet-600',
    rose:    'from-rose-500 to-rose-600',
  };
  const body = (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition">
      <div className={`inline-flex h-1 w-10 rounded-full bg-gradient-to-r ${accents[accent]} mb-3`} />
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-gray-500">{sublabel}</div>}
    </div>
  );
  return href ? <Link href={href} className="block">{body}</Link> : body;
}

function BarRow({ label, count, total, colorClass = 'bg-indigo-500', href }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const labelEl = href ? (
    <Link href={href} className="text-sm text-gray-800 hover:text-indigo-700 font-medium truncate">
      {label}
    </Link>
  ) : (
    <span className="text-sm text-gray-800 font-medium truncate">{label}</span>
  );
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        {labelEl}
        <span className="text-xs text-gray-500 tabular-nums shrink-0">
          {count} <span className="text-gray-400">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClass} rounded-full transition-all`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </div>
  );
}

function BreakdownCard({ title, icon, entries, total, colorFor, hrefFor, emptyMessage }) {
  const list = Object.entries(entries || {});
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <span className="ml-auto text-xs text-gray-400 tabular-nums">{total} total</span>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{emptyMessage || 'No data yet.'}</p>
      ) : (
        <div className="space-y-3">
          {list.map(([key, count]) => (
            <BarRow
              key={key}
              label={key}
              count={count}
              total={total}
              colorClass={typeof colorFor === 'function' ? colorFor(key) : colorFor || 'bg-indigo-500'}
              href={typeof hrefFor === 'function' ? hrefFor(key) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const CourseIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);
const TagIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
  </svg>
);
const UsersIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const DocIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const ClockIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default function SuperadminDashboard({
  totals = {},
  courses_by_status = {},
  courses_by_role = {},
  courses_by_type = {},
  courses_by_license_type = {},
  courses_by_nab_domain = {},
  review_assignments_by_status = {},
  sources_by_priority = {},
  sources_by_type = {},
  ceu_stats = {},
  recent_courses = [],
  pending_reviews = [],
}) {
  const courseTotal = totals.courses || 0;
  const sourceTotal = totals.sources || 0;
  const assignmentTotal = totals.review_assignments || 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">Pipeline health, content catalog, and review activity at a glance.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile
            label="Total courses"
            value={courseTotal}
            sublabel={`${totals.live_courses || 0} launched`}
            href="/superadmin/admin_courses"
            accent="indigo"
          />
          <KpiTile
            label="In review"
            value={totals.in_review_courses || 0}
            sublabel={`${totals.draft_courses || 0} still in draft`}
            href="/superadmin/admin_courses?status=In+Review"
            accent="blue"
          />
          <KpiTile
            label="Reviewers"
            value={totals.reviewers || 0}
            sublabel={`${totals.review_assignments || 0} assignments`}
            href="/superadmin/reviewers"
            accent="violet"
          />
          <KpiTile
            label="Sources"
            value={totals.sources || 0}
            sublabel={`${totals.nab_domains || 0} NAB domains`}
            href="/superadmin/sources"
            accent="emerald"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile
            label="Total CEU hours"
            value={(ceu_stats.total_hours ?? 0).toLocaleString()}
            sublabel={`across ${ceu_stats.total_courses_with_hours || 0} courses`}
            accent="amber"
          />
          <KpiTile
            label="Avg CEU per course"
            value={(ceu_stats.avg_hours ?? 0).toFixed(2)}
            sublabel={`max ${ceu_stats.max_hours ?? 0}`}
            accent="amber"
          />
          <KpiTile
            label="Users"
            value={(totals.users || 0).toLocaleString()}
            sublabel={`${totals.superadmins || 0} superadmins`}
            href="/superadmin/users"
            accent="rose"
          />
          <KpiTile
            label="Pending reviews"
            value={pending_reviews.length}
            sublabel="awaiting reviewer action"
            accent="blue"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BreakdownCard
            title="Courses by status"
            icon={CourseIcon}
            entries={courses_by_status}
            total={courseTotal}
            colorFor={(key) => STATUS_PALETTE[key] || 'bg-indigo-500'}
            hrefFor={(key) => `/superadmin/admin_courses?status=${encodeURIComponent(key)}`}
            emptyMessage="No courses yet."
          />
          <BreakdownCard
            title="Courses by role"
            icon={UsersIcon}
            entries={courses_by_role}
            total={courseTotal}
            colorFor="bg-indigo-500"
            hrefFor={(key) => `/superadmin/admin_courses?role=${encodeURIComponent(key)}`}
          />
          <BreakdownCard
            title="Courses by license type"
            icon={TagIcon}
            entries={courses_by_license_type}
            total={courseTotal}
            colorFor="bg-blue-500"
          />
          <BreakdownCard
            title="Courses by NAB domain"
            icon={TagIcon}
            entries={courses_by_nab_domain}
            total={courseTotal}
            colorFor="bg-violet-500"
          />
          <BreakdownCard
            title="Review assignments by status"
            icon={ClockIcon}
            entries={review_assignments_by_status}
            total={assignmentTotal}
            colorFor={(key) => ASSIGNMENT_PALETTE[key] || 'bg-indigo-500'}
          />
          <BreakdownCard
            title="Courses by type"
            icon={DocIcon}
            entries={courses_by_type}
            total={courseTotal}
            colorFor="bg-emerald-500"
          />
          <BreakdownCard
            title="Sources by priority"
            icon={TagIcon}
            entries={sources_by_priority}
            total={sourceTotal}
            colorFor="bg-amber-500"
          />
          <BreakdownCard
            title="Sources by type"
            icon={DocIcon}
            entries={sources_by_type}
            total={sourceTotal}
            colorFor="bg-rose-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                {ClockIcon}
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Recently updated courses</h2>
            </div>
            {recent_courses.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No courses yet.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {recent_courses.map((c) => (
                  <Link
                    key={c.id}
                    href={`/superadmin/admin_courses/${c.id}`}
                    className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 truncate">{c.name}</p>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                        {c.role && <span>{c.role}</span>}
                        {c.ceu_credit_hours != null && (
                          <span><span className="font-semibold text-gray-700">{c.ceu_credit_hours}</span> CEU hr</span>
                        )}
                        {c.updated_at && <span>Updated {c.updated_at}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                {UsersIcon}
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Pending reviews</h2>
            </div>
            {pending_reviews.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No pending reviews. Nice.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {pending_reviews.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0 flex-1">
                      {a.course ? (
                        <Link
                          href={`/superadmin/admin_courses/${a.course.id}`}
                          className="font-medium text-gray-900 hover:text-indigo-700 truncate block"
                        >
                          {a.course.name}
                        </Link>
                      ) : (
                        <p className="font-medium text-gray-900 italic">Untitled course</p>
                      )}
                      <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                        {a.reviewer && (
                          <Link href={`/superadmin/reviewers/${a.reviewer.id}`} className="hover:text-indigo-700">
                            {a.reviewer.name}
                          </Link>
                        )}
                        {a.assigned_at && <span>Assigned {a.assigned_at}</span>}
                      </div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                      a.status === 'assigned'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
